<template>
  <div class="p-4 space-y-4">
    <!-- Encabezado -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100">Cuentas Bancarias</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Administra tus cuentas bancarias</p>
      </div>
      <button
        @click="abrirModalNueva"
        class="px-3 py-1.5 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors flex items-center gap-1.5"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Nueva Cuenta
      </button>
    </div>

    <!-- Resumen de saldos -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <TarjetaEstadistica
        etiqueta="Saldo Total"
        :monto="saldoTotal"
        color="blue"
      />
      <TarjetaEstadistica
        etiqueta="Cuentas Activas"
        :monto="cuentasActivas.length"
        :es-moneda="false"
        color="green"
      />
      <TarjetaEstadistica
        etiqueta="Total Cuentas"
        :monto="cuentas.length"
        :es-moneda="false"
        color="gray"
      />
    </div>

    <!-- Estado de carga -->
    <div v-if="cargando && cuentas.length === 0" class="text-center py-12">
      <div class="inline-block w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando cuentas...</p>
    </div>

    <!-- Lista de cuentas -->
    <div v-else-if="cuentas.length > 0" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nombre</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Banco</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cuenta</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tipo</th>
              <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Saldo</th>
              <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Estado</th>
              <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr
              v-for="cuenta in cuentas"
              :key="cuenta.id"
              class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <td class="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ cuenta.nombre }}
              </td>
              <td class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                {{ cuenta.banco }}
              </td>
              <td class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 font-mono">
                {{ cuenta.numero_cuenta }}
              </td>
              <td class="px-4 py-2 text-sm">
                <EtiquetaEstado :estado="cuenta.tipo_cuenta" />
              </td>
              <td class="px-4 py-2 text-sm text-right">
                <EtiquetaMonto :monto="cuenta.saldo_actual" :moneda="cuenta.moneda" bold />
              </td>
              <td class="px-4 py-2 text-center">
                <EtiquetaEstado :estado="cuenta.activa ? 'activo' : 'inactivo'" />
              </td>
              <td class="px-4 py-2 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button
                    @click="verDetalle(cuenta)"
                    class="p-1 text-gray-600 hover:text-violet-600 dark:text-gray-400 dark:hover:text-violet-400"
                    title="Ver detalle"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    @click="editarCuenta(cuenta)"
                    class="p-1 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    title="Editar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click="eliminarCuentaConfirm(cuenta.id)"
                    class="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    title="Eliminar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-else class="text-center py-12 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
      <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
      <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">No hay cuentas bancarias registradas</p>
      <button
        @click="abrirModalNueva"
        class="mt-3 px-4 py-2 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700"
      >
        Crear primera cuenta
      </button>
    </div>

    <!-- Modal para Nueva/Editar Cuenta -->
    <div
      v-if="modalAbierto"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="cerrarModal"
    >
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <!-- Header -->
        <div class="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {{ cuentaEditando ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria' }}
          </h2>
          <button
            @click="cerrarModal"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <form @submit.prevent="guardarCuenta" class="p-6 space-y-4">
          <!-- Nombre de la cuenta -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre de la Cuenta *
            </label>
            <input
              v-model="formulario.nombre"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="Ej: Cuenta Principal BBVA"
            />
          </div>

          <!-- Banco y Número de cuenta -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Banco *
              </label>
              <input
                v-model="formulario.banco"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="Ej: BBVA"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Número de Cuenta
              </label>
              <input
                v-model="formulario.numero_cuenta"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="0123456789"
              />
            </div>
          </div>

          <!-- Tipo de cuenta y Moneda -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de Cuenta *
              </label>
              <select
                v-model="formulario.tipo_cuenta"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="corriente">Cuenta Corriente</option>
                <option value="ahorro">Ahorro</option>
                <option value="inversion">Inversión</option>
                <option value="credito">Crédito</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Moneda *
              </label>
              <select
                v-model="formulario.moneda"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="MXN">MXN - Pesos Mexicanos</option>
                <option value="USD">USD - Dólares</option>
                <option value="EUR">EUR - Euros</option>
              </select>
            </div>
          </div>

          <!-- Saldo inicial -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Saldo Inicial *
            </label>
            <input
              v-model.number="formulario.saldo_inicial"
              type="number"
              step="0.01"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="0.00"
            />
          </div>

          <!-- Descripción -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descripción
            </label>
            <textarea
              v-model="formulario.descripcion"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="Notas adicionales sobre esta cuenta..."
            ></textarea>
          </div>

          <!-- Botones -->
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              @click="cerrarModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="cargando"
              class="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span v-if="cargando" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              {{ cuentaEditando ? 'Actualizar' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCuentasBancarias } from '@/composables/finanzas/useCuentasBancarias'
import TarjetaEstadistica from '@/components/Compartidos/TarjetaEstadistica.vue'
import EtiquetaMonto from '@/components/Compartidos/EtiquetaMonto.vue'
import EtiquetaEstado from '@/components/Compartidos/EtiquetaEstado.vue'

const {
  cuentas,
  cargando,
  cuentasActivas,
  saldoTotal,
  cargarCuentas,
  crearCuenta,
  actualizarCuenta,
  eliminarCuenta
} = useCuentasBancarias()

// Estado del modal
const modalAbierto = ref(false)
const cuentaEditando = ref(null)
const formulario = ref({
  nombre: '',
  banco: '',
  numero_cuenta: '',
  tipo_cuenta: 'corriente',
  moneda: 'MXN',
  saldo_inicial: 0,
  descripcion: ''
})

const abrirModalNueva = () => {
  cuentaEditando.value = null
  formulario.value = {
    nombre: '',
    banco: '',
    numero_cuenta: '',
    tipo_cuenta: 'corriente',
    moneda: 'MXN',
    saldo_inicial: 0,
    descripcion: ''
  }
  modalAbierto.value = true
}

const cerrarModal = () => {
  modalAbierto.value = false
  cuentaEditando.value = null
}

const guardarCuenta = async () => {
  if (cuentaEditando.value) {
    await actualizarCuenta(cuentaEditando.value.id, formulario.value)
  } else {
    await crearCuenta(formulario.value)
  }
  cerrarModal()
  await cargarCuentas()
}

const verDetalle = (cuenta) => {
  // TODO: Ver detalle de cuenta
  console.log('Ver detalle:', cuenta)
}

const editarCuenta = (cuenta) => {
  cuentaEditando.value = cuenta
  formulario.value = {
    nombre: cuenta.nombre,
    banco: cuenta.banco,
    numero_cuenta: cuenta.numero_cuenta || '',
    tipo_cuenta: cuenta.tipo_cuenta,
    moneda: cuenta.moneda,
    saldo_inicial: cuenta.saldo_inicial,
    descripcion: cuenta.descripcion || ''
  }
  modalAbierto.value = true
}

const eliminarCuentaConfirm = async (id) => {
  await eliminarCuenta(id)
}

onMounted(async () => {
  await cargarCuentas()
})
</script>
